import type {
  Page,
  PageableInfo,
  TvChannel,
  TvChannelUpdateObj,
} from '@setplex/tria-api'

import { api } from '@setplex/tria-api'
import { combine, createEvent, createStore } from 'effector'
import { model as i18n } from '~/core/i18n'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import { genPagesLogic } from '~/shared/effector'
import { extendMediaItemByLabelPriceTvChannel } from '~/shared/tools'

// "Only favourites" lists are fetched once per page visit, so a channel removed
// from My List while the list is on screen would linger until a reload.
const dropUnfavoritedChannel = (
  page: Page<TvChannel> | null,
  { params }: { params: TvChannelUpdateObj }
) => {
  if (page == null || params.favorite !== false) return page

  const content = page.content.filter((item) => item.id !== params.id)
  return content.length === page.content.length ? page : { ...page, content }
}

export const resetOnlyFavoritesTvChannelsRow = createEvent()
export const $onlyFavoritesTvChannelsRow = createStore<Page<TvChannel> | null>(
  null
)
  .on(api.tv.getOnlyFavoritesTvChannelsRowFx.doneData, (_, page) => page)
  .on(api.tv.updateTvChannelFx.done, dropUnfavoritedChannel)
  .on(ssePurchasesModel.$channel, (page, adjust) => {
    if (page == null) return page
    return {
      ...page,
      content: page.content.map((item) => adjust(item)),
    }
  })
  .reset(resetOnlyFavoritesTvChannelsRow)

export const $onlyFavoritesTvChannelsRowFormatted = combine(
  $onlyFavoritesTvChannelsRow,
  i18n.$t,
  (onlyFavoritesTvChannelsRow) => ({
    ...onlyFavoritesTvChannelsRow,
    content: onlyFavoritesTvChannelsRow?.content.map((item) =>
      extendMediaItemByLabelPriceTvChannel(item)
    ),
  })
)

export const resetOnlyFavoritesTvChannelsGrid = createEvent()
export const $onlyFavoritesTvChannelsGrid = createStore<Page<TvChannel> | null>(
  null
)
  .on(api.tv.getOnlyFavoritesTvChannelsGridFx.doneData, (state, page) => {
    const currentTvChannels = state
      ? [...state.content, ...page.content]
      : page.content

    return {
      ...page,
      content: currentTvChannels,
    }
  })
  .on(api.tv.updateTvChannelFx.done, dropUnfavoritedChannel)
  .on(ssePurchasesModel.$channel, (page, adjust) => {
    if (page == null) return page
    return {
      ...page,
      content: page.content.map((item) => adjust(item)),
    }
  })
  .reset(resetOnlyFavoritesTvChannelsGrid)

export const $onlyFavoritesTvChannelsGridPageInfo =
  createStore<PageableInfo | null>(null).on(
    api.tv.getOnlyFavoritesTvChannelsGridFx.done,
    (
      _,
      { result: { content: _ignored, ...pageInfo }, params: { ...params } }
    ) => {
      return {
        ...pageInfo,
        params,
      }
    }
  )

export const $onlyFavoritesTvChannelsGridFormatted = combine(
  $onlyFavoritesTvChannelsGrid,
  i18n.$t,
  (onlyFavoritesTvChannelsGrid) => {
    const formattedPageContent = onlyFavoritesTvChannelsGrid?.content.map(
      (item) => extendMediaItemByLabelPriceTvChannel(item)
    )

    return {
      ...onlyFavoritesTvChannelsGrid,
      content: formattedPageContent,
    }
  }
)

export const loadMoreOnlyFavoritesTvChannelsGrid = genPagesLogic(
  $onlyFavoritesTvChannelsGridPageInfo,
  api.tv.getOnlyFavoritesTvChannelsGridFx
)
